import { Grid, ThemeProvider, Tab, lighten } from '@mui/material';
import { Tabs } from '@components/tabs';

import { hideScrollBars } from '@theme/styles';
import { useComposeTheme } from '@theme/theme';
import { useStore } from '@store/GlobalStore';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { shallow } from 'zustand/shallow';
import { DrawerList } from '@pages/QuoteDetails/constants/DrawerList.enum';
import { QuoteDrawerUrlParams } from '@pages/QuoteDetails/constants/drawerListItems';
import { QuoteType } from '@models/quotes';
import { QuoteResponse } from '@store/Quote/Quote.types';

interface UnderwriterToolkitTabsProps {
  quote?: QuoteResponse;
}

export const UnderwriterToolkitTabs = ({ quote }: UnderwriterToolkitTabsProps) => {
  const darkTheme = useComposeTheme('dark');
  const { isInternalUser, isUnderwriter } = useStore((state) => state.userAuth, shallow);
  const [setLayoutSelectedTab, selectedLayoutTab] = useQuoteStore((s) => [s.setLayoutSelectedTab, s.selectedLayoutTab]);

  const sidebarItem = new URLSearchParams(window.location.search).get('sidebarItem');
  const sidebarPinned = new URLSearchParams(window.location.search).get('sidebarPinned');

  const tabValues = {
    ['quoteDetails']: {
      label: 'Quote Details',
      visible: true,
      isDisabled: false,
    },
    [QuoteDrawerUrlParams.Summary]: {
      label: 'Summary',
      visible: true,
      isDisabled: sidebarItem === QuoteDrawerUrlParams.Summary || sidebarPinned === QuoteDrawerUrlParams.Summary,
    },
    [QuoteDrawerUrlParams.Subjectivities]: {
      label: 'Subjectivities',
      visible: isInternalUser && quote?.quoteType !== QuoteType.Endorsement,
      isDisabled:
        sidebarItem === QuoteDrawerUrlParams.Subjectivities || sidebarPinned === QuoteDrawerUrlParams.Subjectivities,
    },
    [QuoteDrawerUrlParams.Underwriting]: {
      label: 'Underwriting',
      visible: isInternalUser,
      isDisabled:
        sidebarItem === QuoteDrawerUrlParams.Underwriting || sidebarPinned === QuoteDrawerUrlParams.Underwriting,
    },
    [QuoteDrawerUrlParams.Tools]: {
      label: 'Technical Details',
      visible: isInternalUser,
      isDisabled: sidebarItem === QuoteDrawerUrlParams.Tools || sidebarPinned === QuoteDrawerUrlParams.Tools,
    },
    [QuoteDrawerUrlParams.Messages]: {
      label: 'Messages',
      visible: quote?.quoteType !== QuoteType.Endorsement,
      isDisabled: sidebarItem === QuoteDrawerUrlParams.Messages || sidebarPinned === QuoteDrawerUrlParams.Messages,
    },
    [QuoteDrawerUrlParams.Notes]: {
      label: 'Notes',
      visible: isInternalUser || isUnderwriter,
      isDisabled: sidebarItem === QuoteDrawerUrlParams.Notes || sidebarPinned === QuoteDrawerUrlParams.Notes,
    },
    [QuoteDrawerUrlParams.RelatedTransactions]: {
      label: 'Related Transactions',
      visible: isInternalUser,
      isDisabled:
        sidebarItem === QuoteDrawerUrlParams.RelatedTransactions ||
        sidebarPinned === QuoteDrawerUrlParams.RelatedTransactions,
    },
  };

  const visibleTabs = Object.entries(tabValues).filter(([_, config]) => config.visible);

  return (
    <ThemeProvider theme={darkTheme}>
      <Grid
        sx={{
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          justifyContent: 'space-between',
          display: 'flex',
          backgroundColor: (theme) => theme.palette.background.light,
          flexDirection: 'row',
          height: '30px',
          minHeight: '30px', // Ensure minimum height for the header
          ...hideScrollBars,

          '.MuiTabs-fixed': {
            minHeight: '30px',
            height: '30px',
          },
          '.MuiTabs-root': {
            minHeight: '30px',
            height: '30px',
          },
        }}
      >
        <Tabs
          type='header'
          disableRipple
          value={selectedLayoutTab}
          onChange={(_event, newValue) => {
            setLayoutSelectedTab(newValue);
          }}
          slotProps={{
            indicator: {
              style: { display: 'none' },
            },
          }}
        >
          {visibleTabs.map(([tabKey, config]) => (
            <Tab
              key={tabKey}
              label={config.label}
              disabled={config.isDisabled}
              value={tabKey as DrawerList}
              tabIndex={0}
              sx={[
                config.isDisabled
                  ? {
                      color: (theme) => `${lighten(theme.palette.background.light, 0.4)} !important`,
                      textDecoration: 'underline',
                      textUnderlineOffset: '4px',
                      textDecorationThickness: '1px',
                    }
                  : {
                      // on hover text underline
                      '&:hover': {
                        textDecoration: config.isDisabled ? 'none' : 'underline',
                        textUnderlineOffset: '4px',
                        textDecorationThickness: '1px',
                      },
                    },
                {
                  minHeight: '30px',
                  minWidth: 'auto',
                  height: '30px',
                  '&.Mui-selected': {
                    position: 'relative',
                    fontWeight: 500,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      width: '80%',
                      textTransform: 'translateX(-50%)',
                      height: '2px',
                      bottom: 5,
                      backgroundColor: (theme) => theme.palette.accent.secondary,
                    },
                  },
                },
              ]}
            />
          ))}
        </Tabs>
      </Grid>
    </ThemeProvider>
  );
};
